import { useClerk } from "@clerk/clerk-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "~/components/ui/button";
import {
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { useTRPC } from "~/lib/trpc";
import { isWebsiteUrl } from "~/utils/url-utils";

export const Route = createFileRoute("/onboarding/provide-website")({
  component: RouteComponent,
});

type FormValues = {
  website: string;
};

const formSchema = z.object({
  website: z
    .string()
    .min(1, { message: "Website URL is required" })
    .refine(isWebsiteUrl, { message: "Please enter a valid website URL" }),
});

function RouteComponent() {
  const clerk = useClerk();
  const trpc = useTRPC();
  const mutation = useMutation(
    trpc.onboarding.completeWebsiteStep.mutationOptions({
      onError: () => null,
    }),
  );

  const form = useForm<FormValues>({
    defaultValues: {
      website: "",
    },
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async (data: FormValues) => {
    await mutation.mutateAsync(data, {
      onSuccess: async ({ orgId }) => {
        await clerk.setActive({ organization: orgId });
      },
      onError: (error) => {
        if (
          error.data?.code === "BAD_REQUEST" ||
          error.data?.code === "NOT_FOUND"
        ) {
          form.setError("website", {
            type: "manual",
            message: error.message,
          });
        } else {
          toast.error(error.message);
        }
      },
    });
  };

  return (
    <>
      <CardHeader>
        <CardTitle>Enter your website</CardTitle>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CardContent>
            <FormField
              control={form.control}
              name="website"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Website URL</FormLabel>
                  <FormControl>
                    <Input placeholder="example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="animate-spin" />}
              Continue
            </Button>
          </CardFooter>
        </form>
      </Form>
    </>
  );
}
